import { zodResolver } from '@hookform/resolvers/zod';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Flame } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from 'semantic-ui-react';

import CancelButton from '@components/cancel-button';
import ErrMessage from '@components/err-message';
import { pickDirtyValues } from '@components/form';
import Modal, { useModal } from '@components/modal';
import raiseToast from '@components/toast';

import {
  useDeleteVersionVulnerabilityAnnotationMutation,
  useGetVersionVulnerabilityAnnotationQuery,
} from '../../applications.api';
import { useUpsertVersionVulnerabilityAnnotationMutation } from '../../utils';

import { VulnerabilityAnnotationForm } from './vulnerability-annotation-form';
import {
  type VulnerabilityAnnotationFormValues,
  vulnerabilityAnnotationDefaultValues,
  vulnerabilityAnnotationFormSchema,
} from './vulnerability-annotation-form.schema';
import s from './vulnerability-annotation-upsert-modal.scss';

const MODAL_ID = 'vulnerabilityAnnotationUpsert';

export const VulnerabilityAnnotationUpsertModal = () => {
  useStyles(s);

  const { close, data, id, isOpen } = useModal(MODAL_ID);

  const annotationId = data?.annotationId;
  const isEditing = !!annotationId;

  const {
    control,
    formState: { dirtyFields },
    handleSubmit,
    reset,
    watch,
  } = useForm<VulnerabilityAnnotationFormValues>({
    defaultValues: vulnerabilityAnnotationDefaultValues,
    resolver: zodResolver(vulnerabilityAnnotationFormSchema),
  });

  const {
    currentData: annotation,
    error: loadError,
    isError: isLoadError,
    isFetching,
  } = useGetVersionVulnerabilityAnnotationQuery(
    {
      annotationId: annotationId ?? '',
      appId: data?.appId ?? '',
      versionId: data?.versionId ?? '',
    },
    { skip: !isOpen || !isEditing },
  );

  // Set after the form is reset with the loaded annotation so edit mode
  // doesn't briefly render delete-mode against the null-status defaults.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setHydrated(false);
      return;
    }
    if (isEditing && (isFetching || isLoadError)) return;

    if (annotation) {
      reset({
        actionStatement: annotation.actionStatement,
        additionalDetails: annotation.additionalDetails,
        impactStatement: annotation.impactStatement,
        justification: annotation.justification,
        status: annotation.status,
        statusNotes: annotation.statusNotes,
      });
    } else {
      reset(vulnerabilityAnnotationDefaultValues);
    }
    setHydrated(true);
  }, [annotation, isEditing, isFetching, isLoadError, isOpen, reset]);

  const [
    upsert,
    { error: upsertError, isLoading: isUpserting, reset: resetUpsert },
  ] = useUpsertVersionVulnerabilityAnnotationMutation();
  const [
    deleteAnnotation,
    { error: deleteError, isLoading: isDeleting, reset: resetDeleteAnnotation },
  ] = useDeleteVersionVulnerabilityAnnotationMutation();

  const isSubmitting = isUpserting || isDeleting;
  const submitError = upsertError ?? deleteError;

  const watchedStatus = watch('status');
  const isInFormDelete = hydrated && isEditing && watchedStatus === null;

  const onSubmit = handleSubmit(async values => {
    if (!data) return;

    if (isInFormDelete) {
      const result = await deleteAnnotation({
        annotationId: annotationId!,
        appId: data.appId,
        versionId: data.versionId,
        vulnerabilityId: data.vulnerabilityId,
      });
      if (!result.error) {
        raiseToast.success(
          'The vulnerability annotation has been successfully deleted.',
        );
        close();
      }
      return;
    }

    if (values.status === null) return;

    // Re-spread status so its type carries the null check above; the update
    // request no longer accepts a null status.
    const submitValues = { ...values, status: values.status };

    const result = await upsert(
      isEditing
        ? {
            ...pickDirtyValues(dirtyFields, submitValues),
            annotationId,
            appId: data.appId,
            versionId: data.versionId,
            vulnerabilityId: data.vulnerabilityId,
          }
        : {
            ...submitValues,
            appId: data.appId,
            packageName: data.packageRef.name,
            packageType: data.packageRef.type,
            packageVersion: data.packageRef.version,
            versionId: data.versionId,
            vulnerabilityId: data.vulnerabilityId,
          },
    );

    if (!result.error) {
      raiseToast.success(
        isEditing
          ? 'The vulnerability annotation has been successfully updated.'
          : 'The vulnerability annotation has been successfully created.',
      );
      close();
    }
  });

  const isLoadingAnnotation = isEditing && !hydrated;

  return (
    <Modal
      aria-label="Vulnerability Annotation Form"
      closeOnDimmerClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      id={id}
      onAfterClose={() => {
        reset(vulnerabilityAnnotationDefaultValues);
        resetUpsert();
        resetDeleteAnnotation();
      }}
      size="small"
    >
      <Modal.Header
        extra={
          data && (
            <div className={s.subheader}>
              <div className={s.cve}>{data.vulnerabilityId}</div>
              <div className={s.package}>
                Package: {data.packageRef.name} {data.packageRef.version}
              </div>
            </div>
          )
        }
        icon={<Flame color="var(--sui-color-teal)" />}
        title="Vulnerability Exploit Statement"
      />

      <Modal.Content>
        <ErrMessage error={loadError ?? false} />

        <ErrMessage error={submitError ?? false} />

        <VulnerabilityAnnotationForm
          control={control}
          disabled={isLoadingAnnotation}
          isEditing={isEditing}
        />
      </Modal.Content>

      <Modal.Actions>
        <CancelButton
          content="Cancel"
          disabled={isSubmitting}
          onClick={close}
        />
        {isInFormDelete ? (
          <Button
            color="red"
            content="Delete"
            disabled={isSubmitting}
            icon="trash"
            loading={isSubmitting}
            onClick={onSubmit}
          />
        ) : (
          <Button
            color="blue"
            content="Save"
            // None is a selectable value, so disable on null rather than
            // letting validation fire a misleading "required" error.
            disabled={
              isSubmitting || isLoadingAnnotation || watchedStatus === null
            }
            loading={isSubmitting}
            onClick={onSubmit}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default VulnerabilityAnnotationUpsertModal;
